import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Check, X, Loader2, UserX, AlertTriangle } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import SuperAdminOnlyNote from "../components/SuperAdminOnlyNote";
import api from "../services/api";
import { isSuperAdmin } from "../utils/role";
import { formatLagosDateTime } from "../utils/tripLog";

// All data comes from the backend (the "bucket"). Nothing is hardcoded.

const STATUS_TABS = [
  { key: "pending_review", label: "Pending review" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "", label: "All" },
];

const STATUS_BADGE = {
  pending_review: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-gray-200 text-gray-700",
};

const STATUS_LABEL = {
  pending_review: "Pending review",
  approved: "Approved",
  rejected: "Rejected",
};

const formatDate = (d) =>
  formatLagosDateTime(d, "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const DeletionRequests = () => {
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState(null);
  const canEdit = isSuperAdmin();

  const load = async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/v1/admin/deletion-requests${qs}`);
      setRequests(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to load deletion requests"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const act = async (id, action) => {
    if (!canEdit) return;
    const verb = action === "approve" ? "approve (delete account)" : "reject";
    if (!window.confirm(`Are you sure you want to ${verb} this request?`))
      return;
    setActioningId(id);
    try {
      await api.post(`/v1/admin/deletion-requests/${id}/${action}`, {});
      toast.success(
        action === "approve"
          ? "Account deleted"
          : "Request rejected"
      );
      load();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || `Could not ${action} the request`
      );
    } finally {
      setActioningId(null);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Account Deletion Requests
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Users whose self-service deletion was blocked (wallet balance,
              owed payment, active ride/booking) appear here. Resolve the
              blockers, then approve to delete the account, or reject to keep it.
            </p>
            {!canEdit && <SuperAdminOnlyNote className="mt-3" />}
          </div>

          {/* Status tabs */}
          <div className="flex flex-wrap gap-2 mb-5">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  statusFilter === tab.key
                    ? "bg-black text-white"
                    : "bg-white text-gray-600 hover:bg-gray-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* List */}
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <UserX className="w-10 h-10 mb-3" />
              <p>No deletion requests here.</p>
            </div>
          ) : (
            <div className="space-y-4 max-w-4xl">
              {requests.map((r) => (
                <div
                  key={r._id}
                  className="bg-white dark:bg-black rounded-xl shadow-sm p-5"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {r.userName || "Unknown user"}
                        </span>
                        <span
                          className={`text-[11px] px-2 py-0.5 rounded-full ${
                            STATUS_BADGE[r.status] || "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {STATUS_LABEL[r.status] || r.status}
                        </span>
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {r.role}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {r.userEmail}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Requested {formatDate(r.createdAt)}
                      </p>
                    </div>

                    {r.status === "pending_review" && canEdit && (
                      <div className="flex gap-2">
                        <button
                          disabled={actioningId === r._id}
                          onClick={() => act(r._id, "approve")}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                        >
                          {actioningId === r._id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          Approve &amp; delete
                        </button>
                        <button
                          disabled={actioningId === r._id}
                          onClick={() => act(r._id, "reject")}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
                        >
                          <X className="w-4 h-4" />
                          Reject
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Blockers */}
                  {Array.isArray(r.blockers) && r.blockers.length > 0 && (
                    <div className="mt-4 border-t border-gray-100 pt-3">
                      <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                        Blockers to resolve
                      </p>
                      <ul className="space-y-1">
                        {r.blockers.map((b, i) => (
                          <li
                            key={i}
                            className="text-sm text-gray-700 dark:text-gray-300"
                          >
                            • {b.detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {r.adminNote && (
                    <p className="text-xs text-gray-400 mt-3">
                      Note: {r.adminNote}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default DeletionRequests;
